import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@modules/iam';
import { validatePasswordChange, type PasswordChangeInput } from '../domain/password';
import { authRepository } from '../infrastructure/auth.repository';
import { apiErrorMessage } from './apiError';

/** UC-4: 3 validaciones i18n (T7, orden exacto) + PUT /auth/change-password + refreshUser. */
export function useChangePassword() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = useCallback(
    async (input: PasswordChangeInput): Promise<boolean> => {
      const code = validatePasswordChange(input);
      if (code === 'required') {
        setError(t('auth.allFieldsRequired'));
        return false;
      }
      if (code === 'tooShort') {
        setError(t('auth.minPasswordLength'));
        return false;
      }
      if (code === 'mismatch') {
        setError(t('auth.passwordMismatch'));
        return false;
      }
      setLoading(true);
      setError('');
      try {
        await authRepository.changePassword(input.currentPassword, input.newPassword);
        await refreshUser();
        return true;
      } catch (err: unknown) {
        setError(apiErrorMessage(err) || t('auth.changePasswordError'));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [t, refreshUser]
  );

  return { error, loading, submit, forcePasswordChange: Boolean(user?.forcePasswordChange) };
}
